import { useCallback, useEffect, useRef } from 'react';

import { UseImageUploaderProvider } from 'components/widgets/ImageUploader/ImageUploader.provider';

export const IMAGE_UPLOADER_TAB_UPLOAD = 'upload';
export const IMAGE_UPLOADER_TAB_FROM_URL = 'fromUrl';

export type ImageUploaderSourceTab = typeof IMAGE_UPLOADER_TAB_UPLOAD | typeof IMAGE_UPLOADER_TAB_FROM_URL;

interface Props {
    ui: UseImageUploaderProvider['ui'];
    api: UseImageUploaderProvider['api'];
}

export const useImageUploaderLogic = ({ ui, api }: Props) => {
    // Tracks the active blob: URL so we can revoke it and avoid memory leaks.
    const lastObjectUrlRef = useRef<string | null>(null);

    // Revoke any remaining object URL when the widget unmounts.
    useEffect(() => {
        return () => {
            if (lastObjectUrlRef.current) {
                URL.revokeObjectURL(lastObjectUrlRef.current);
            }
        };
    }, []);

    // Sync existing server image into local state when editing a widget.
    // Both imageUrl (preview) and imageFile (upload payload on save) must be set —
    // the download API returns a Blob, but save sends a File; without imageFile, save sends null and clears the image.
    useEffect(() => {
        const blob = api.getImage.instance.data;
        if (!blob || !(blob instanceof Blob) || blob.size === 0) {
            ui.setters.setImageUrl(null);
            ui.setters.setImageFile(null);
            return;
        }

        if (lastObjectUrlRef.current) {
            URL.revokeObjectURL(lastObjectUrlRef.current);
        }

        const extension = blob.type?.split('/')[1] || 'png';
        const file = new File([blob], `image.${extension}`, {
            type: blob.type || 'image/png',
        });
        const objectUrl = URL.createObjectURL(file);
        lastObjectUrlRef.current = objectUrl;
        ui.setters.setImageFile(file);
        ui.setters.setImageUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
            if (lastObjectUrlRef.current === objectUrl) {
                lastObjectUrlRef.current = null;
            }
        };
    }, [api.getImage.instance.data, ui.setters.setImageFile, ui.setters.setImageUrl]);

    const handleFileFromDropzone = useCallback(
        (file: File) => {
            if (lastObjectUrlRef.current) {
                URL.revokeObjectURL(lastObjectUrlRef.current);
            }
            const objectUrl = URL.createObjectURL(file);
            lastObjectUrlRef.current = objectUrl;
            ui.setters.setImageFile(file);
            ui.setters.setImageUrl(objectUrl);
        },
        [ui.setters],
    );

    const handleUrlLoad = useCallback(
        async (url: string) => {
            if (lastObjectUrlRef.current) {
                URL.revokeObjectURL(lastObjectUrlRef.current);
                lastObjectUrlRef.current = null;
            }

            const response = await fetch(url);
            const blob = await response.blob();

            const file = new File([blob], 'image.png', {
                type: blob.type || 'image/png',
            });

            const objectUrl = URL.createObjectURL(file);
            lastObjectUrlRef.current = objectUrl;

            ui.setters.setImageFile(file);
            ui.setters.setImageUrl(objectUrl);
        },
        [ui.setters],
    );

    const handleClearImage = useCallback(() => {
        if (lastObjectUrlRef.current) {
            URL.revokeObjectURL(lastObjectUrlRef.current);
            lastObjectUrlRef.current = null;
        }
        ui.setters.setImageFile(null);
        ui.setters.setImageUrl(null);
    }, [ui.setters]);

    return {
        logic: {
            handleFileFromDropzone,
            handleUrlLoad,
            handleClearImage,
        },
    };
};
